import { Injectable, inject } from '@angular/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { default as packageJson } from '../../../package.json';
import { AuthService } from './auth.service';
import { InstructionContentService } from './instruction-content.service';
import {
  PrintTranslateFn,
  PrintTranslationService
} from './print-translation.service';
import {
  INSTRUCTION_PAGES,
  instructionTitleKey,
  sectionsWithPages
} from '../modules/help/instruction-catalog';
import {
  ManualSection,
  buildManualDocument
} from '../modules/help/manual-document';

/** How long the print frame may wait for the images and fonts. */
const LOAD_TIMEOUT_MS = 30000;

/** Languages the whole manual is printed in; labels in their own language. */
export const MANUAL_LANGUAGES: ReadonlyArray<{ code: string; label: string }> =
  [
    { code: 'fi', label: 'Suomi' },
    { code: 'en', label: 'English' }
  ];

/** The UI language when the manual has it, else English. */
export const defaultManualLanguage = (uiLanguage: string): string =>
  MANUAL_LANGUAGES.some((l) => l.code === uiLanguage) ? uiLanguage : 'en';

/**
 * Prints the whole user manual: every page of the help menu in its order,
 * in Finnish or English whatever the UI language (a page without that
 * language version comes in the fallback language, as in the help
 * drawer), with a cover (version, print date) and a table of contents.
 * The browser's print window prints it on paper or saves it as a PDF.
 *
 * Only for Aline's own super admins (system_admin): the manual must not be
 * printed and passed on by the clinics' users.
 */
@Injectable({
  providedIn: 'root'
})
export class InstructionPrintService {
  private contentService = inject(InstructionContentService);
  private printTranslation = inject(PrintTranslationService);
  private authService = inject(AuthService);

  /** Whether the user may print the whole manual. */
  canPrintManual(): boolean {
    return this.authService.isAdmin();
  }

  async printManual(lang: string): Promise<void> {
    if (!this.canPrintManual()) {
      throw new Error('Printing the manual is for super admins only.');
    }
    const t = await this.printTranslation.getTranslator(lang);
    const text = (key: string, params?: Record<string, unknown>) =>
      t(`common.instructions-browser.${key}`, params);
    const html = buildManualDocument({
      lang,
      baseUrl: window.location.origin,
      logoUrl: 'assets/images/aline_logo_navy.png',
      version: packageJson.version,
      texts: {
        title: text('export-pdf-title'),
        subtitle: text('export-pdf-subtitle'),
        version: text('export-pdf-version', {
          version: packageJson.version
        }),
        printed: text('export-pdf-printed', {
          // Day first in English too (26/10/2026), as elsewhere in Aline.
          date: new Date().toLocaleDateString(lang === 'en' ? 'en-GB' : lang)
        }),
        contents: text('export-pdf-contents'),
        missing: text('export-pdf-missing')
      },
      sections: await this.loadSections(lang, t)
    });
    await this.print(html);
  }

  private async loadSections(
    lang: string,
    t: PrintTranslateFn
  ): Promise<ManualSection[]> {
    const pages = await firstValueFrom(
      forkJoin(
        INSTRUCTION_PAGES.map((page) =>
          this.contentService.getContent(page.slug, lang)
        )
      )
    );
    const htmlBySlug = new Map(
      INSTRUCTION_PAGES.map((page, i) => [page.slug, pages[i]])
    );
    return sectionsWithPages().map((section) => ({
      id: section.id,
      label: t(section.labelKey),
      chapters: INSTRUCTION_PAGES.filter((p) => p.section === section.id).map(
        (page) => ({
          slug: page.slug,
          title: this.pageTitle(page.slug, t),
          sub: !!page.parent,
          html: htmlBySlug.get(page.slug) ?? null
        })
      )
    }));
  }

  /** Prints the document from a hidden frame once its images and fonts are in. */
  private print(html: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.style.cssText =
        'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
      frame.onload = async () => {
        const win = frame.contentWindow;
        const doc = frame.contentDocument;
        if (!win || !doc) {
          frame.remove();
          reject(new Error('The print frame did not open.'));
          return;
        }
        try {
          await Promise.race([
            Promise.all([
              ...Array.from(doc.images).map((image) =>
                image.complete
                  ? Promise.resolve()
                  : new Promise((done) => {
                      image.onload = image.onerror = done;
                    })
              ),
              doc.fonts.ready
            ]),
            new Promise((done) => setTimeout(done, LOAD_TIMEOUT_MS))
          ]);
          // print() returns when the print window closes (Chrome, Firefox);
          // the frame goes then, or after a while where it does not block.
          // The browser names the saved PDF after the page title.
          const appTitle = document.title;
          let printing = true;
          const done = () => {
            if (printing) {
              printing = false;
              document.title = appTitle;
              frame.remove();
            }
          };
          win.addEventListener('afterprint', done, { once: true });
          document.title = doc.title;
          win.focus();
          win.print();
          setTimeout(done, 60000);
          resolve();
        } catch (error) {
          frame.remove();
          reject(error);
        }
      };
      frame.srcdoc = html;
      document.body.appendChild(frame);
    });
  }

  private pageTitle(slug: string, t: PrintTranslateFn): string {
    const key = instructionTitleKey(slug);
    const title = t(key);
    return title === key ? slug : title;
  }
}
